/** Scoped merchant question/answer contracts; no synthetic or calculated observations are relabelled here. */
import * as z from 'zod';
export const STORE_KNOWLEDGE_CAPABILITY = 'store-knowledge.v1';
const id = z.string().trim().min(1).max(256);
export const knowledgeSubjectSchema = z.strictObject({
    productId: z.string().regex(/^gid:\/\/shopify\/Product\/[^\s/]+$/),
    variantId: z
        .string()
        .regex(/^gid:\/\/shopify\/ProductVariant\/[^\s/]+$/)
        .nullable(),
});
export const knowledgeScopeSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('all_observed') }),
    z.strictObject({
        kind: z.literal('order_interval'),
        start: z.iso.datetime({ offset: true }),
        end: z.iso.datetime({ offset: true }),
    }),
    z.strictObject({ kind: z.literal('unresolved'), description: z.string().trim().min(1).max(1000) }),
]);
export const knowledgeRoleSchema = z.enum(['sample', 'gift', 'packaging', 'replacement', 'retail', 'other']);
export const knowledgeResponseSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('not_sample'), note: z.string().trim().max(2000) }),
    z.strictObject({
        kind: z.literal('answer'),
        role: knowledgeRoleSchema,
        targetState: z.enum(['selected', 'none', 'unknown']),
        targets: z.array(knowledgeSubjectSchema).max(20),
        note: z.string().trim().max(2000),
    }),
    z.strictObject({ kind: z.literal('skip'), note: z.string().trim().max(2000) }),
    z.strictObject({ kind: z.literal('not_sure'), note: z.string().trim().max(2000) }),
    z.strictObject({ kind: z.literal('withdraw'), note: z.string().trim().max(2000) }),
]);
export const storeKnowledgeWriteSchema = z
    .strictObject({
    interaction: z.literal('sample_check').optional(),
    origin: z.enum(['suggested', 'manual', 'reopen']).optional(),
    mutationId: id,
    questionId: id.nullable(),
    expectedVersion: z.number().int().nonnegative(),
    subject: knowledgeSubjectSchema,
    scope: knowledgeScopeSchema,
    response: knowledgeResponseSchema,
})
    .superRefine((value, ctx) => {
    const fail = (message) => ctx.addIssue({ code: 'custom', message });
    if (value.scope.kind === 'order_interval' && Date.parse(value.scope.end) <= Date.parse(value.scope.start))
        fail('The end must be after the start');
    if (value.origin === 'reopen' && !value.questionId)
        fail('Reopening requires an existing question');
    if (value.interaction === 'sample_check' && value.scope.kind !== 'unresolved')
        fail('A quick sample answer does not define which orders used the item as a sample');
    if (value.response.kind === 'withdraw' && !value.questionId)
        fail('Only an existing answer can be withdrawn');
    const r = value.response;
    if (r.kind === 'not_sample' && value.interaction !== 'sample_check')
        fail('Not-a-sample answers require the sample question');
    if (value.interaction === 'sample_check' && r.kind === 'answer' && r.role !== 'sample')
        fail('The sample question cannot assign a different item use');
    if (r.kind !== 'answer')
        return;
    if (r.role !== 'sample' && (r.targetState !== 'none' || r.targets.length))
        fail('Only a sample can promote retail targets');
    if ((r.targetState === 'selected') !== r.targets.length > 0)
        fail('Select targets or explicitly choose none/unknown');
    if (r.role === 'other' && !r.note)
        fail('Describe the other use');
    const keys = r.targets.map((t) => JSON.stringify(t));
    if (new Set(keys).size !== keys.length)
        fail('Targets must be distinct');
});
export const knowledgeActorSchema = z.strictObject({ type: z.enum(['shopify_user', 'operator']), id });
export const knowledgeCatalogItemSchema = z.strictObject({
    subject: knowledgeSubjectSchema,
    title: z.string().nullable(),
    productTitle: z.string().nullable(),
    imageUrl: z.string().nullable(),
    productType: z.string().nullable(),
    tags: z.array(z.string()),
    freeOrders: z.number().int().nonnegative(),
    sourceStamp: z.string(),
});
export const knowledgeReadInputSchema = z.strictObject({
    suggestedOnly: z.boolean().default(true),
    search: z.string().trim().max(200).default(''),
    after: z.string().max(512).default(''),
    subject: knowledgeSubjectSchema.optional(),
    entriesAfter: z.string().max(256).default(''),
    questionId: id.optional(),
    beforeVersion: z.number().int().positive().optional(),
});
export const knowledgeRelationshipSchema = z.strictObject({
    version: z.number().int().positive(),
    state: z.enum(['confirmed', 'unresolved', 'withdrawn']),
    subject: knowledgeSubjectSchema,
    scope: knowledgeScopeSchema,
    sampleStatus: z.enum(['yes', 'no', 'unknown']),
    role: knowledgeRoleSchema.nullable(),
    targetState: z.enum(['selected', 'none', 'unknown']),
    targets: z.array(knowledgeSubjectSchema),
    sourceAnswerId: id,
});
export const knowledgeAnswerRecordSchema = z.strictObject({
    id,
    questionId: id,
    version: z.number().int().positive(),
    mutationId: id,
    actor: knowledgeActorSchema,
    recordedAt: z.iso.datetime({ offset: true }),
    scope: knowledgeScopeSchema,
    response: knowledgeResponseSchema,
    origin: z.enum(['suggested', 'manual', 'reopen']),
    supersedesAnswerId: id.nullable(),
    candidateEvidence: z.strictObject({
        rule: z.literal('sample-candidate'),
        version: z.literal(1),
        clues: z.array(z.strictObject({ field: z.string(), value: z.string() })),
    }),
    questionSnapshot: z.array(z.strictObject({
        key: id,
        version: z.number().int().positive(),
        text: z.string(),
        predicate: id,
        purpose: z.string(),
        options: z.array(z.strictObject({ value: id, label: z.string() })),
    })),
    sourceRevision: z.string(),
    subjectSnapshot: knowledgeCatalogItemSchema,
    targetSnapshots: z.array(knowledgeCatalogItemSchema),
    relationshipVersion: z.number().int().positive(),
});
export const knowledgeEntrySchema = z.strictObject({
    questionId: id,
    subject: knowledgeSubjectSchema,
    version: z.number().int().positive(),
    relationship: knowledgeRelationshipSchema,
    answers: z.array(knowledgeAnswerRecordSchema),
    answersBefore: z.number().int().positive().nullable(),
});
export const storeKnowledgeReadSchema = z.strictObject({
    organizationId: id,
    connectionId: id,
    generation: z.number().int().nonnegative(),
    sourceRevision: z.string(),
    catalog: z.array(knowledgeCatalogItemSchema),
    next: z.string().nullable(),
    entries: z.array(knowledgeEntrySchema),
    entriesNext: z.string().nullable(),
});
//# sourceMappingURL=store-knowledge.js.map